import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import type { SettingsAuditEntry } from '@ace-tracker/shared';
import { Button } from '../Button';

export const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-sky-blue';

const fieldBase = `min-h-[44px] w-full rounded-input border bg-surface px-3 text-sm text-text-default placeholder:text-text-faint disabled:opacity-60 ${focusRing}`;

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function TextInput(
  { invalid, className = '', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type="text"
      aria-invalid={invalid || undefined}
      className={`${fieldBase} ${invalid ? 'border-error' : 'border-border'} ${className}`}
      {...props}
    />
  );
});

export function SelectInput({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`${fieldBase} border-border ${className}`} {...props}>
      {children}
    </select>
  );
}

/** True for '' (unset, where allowed) or a finite, non-negative number. */
export function isValidAmount(value: string, allowEmpty = false): boolean {
  if (value.trim() === '') return allowEmpty;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}

/** Price input: a text field with decimal keyboard, so partial input like "0." isn't lost while typing. */
export function AmountInput({
  value,
  onChange,
  allowEmpty = false,
  label,
  className = '',
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  label: string;
}) {
  return (
    <TextInput
      inputMode="decimal"
      aria-label={label}
      value={value}
      invalid={!isValidAmount(value, allowEmpty)}
      onChange={(e) => onChange(e.target.value)}
      className={`text-right tabular-nums ${className}`}
      {...props}
    />
  );
}

export function amountToString(value: number | undefined): string {
  return value === undefined ? '' : String(value);
}

export function stringToAmount(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

export function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-text-default">
        {label}
      </label>
      {hint && <p className="mt-0.5 text-xs text-text-muted">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function Card({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <div className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <h3 className="text-sm font-medium text-text-default">{title}</h3>
      {description && <div className="mt-1 text-xs text-text-muted">{description}</div>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function AuditLine({ entry }: { entry?: SettingsAuditEntry }) {
  return (
    <p className="text-xs text-text-faint">
      {entry ? (
        <>
          Last saved by <b className="font-medium text-text-muted">{entry.updatedBy}</b> on{' '}
          {new Date(entry.updatedAt).toLocaleString()}
        </>
      ) : (
        'Using built-in defaults — not saved yet'
      )}
    </p>
  );
}

/**
 * Save / Discard bar at the bottom of each settings section. Status text is
 * announced politely; errors are shown as text (never color alone).
 */
export function SaveBar({
  dirty,
  saving,
  error,
  savedAt,
  audit,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  saving: boolean;
  error?: string;
  /** Set right after a successful save, to confirm it. */
  savedAt?: number;
  audit?: SettingsAuditEntry;
  onSave: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="sticky bottom-0 z-10 -mx-4 mt-6 flex flex-wrap items-center gap-4 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-card sm:border">
      <div className="mr-auto min-w-0" aria-live="polite">
        {error ? (
          <p className="text-sm font-medium text-error">
            <i aria-hidden="true" className="fa-solid fa-circle-exclamation mr-1.5" />
            {error}
          </p>
        ) : dirty ? (
          <p className="text-sm text-text-muted">
            <i aria-hidden="true" className="fa-solid fa-pen mr-1.5" />
            Unsaved changes
          </p>
        ) : savedAt ? (
          <p className="text-sm text-text-muted">
            <i aria-hidden="true" className="fa-solid fa-check mr-1.5 text-success" />
            Saved. Dashboard figures now use these settings.
          </p>
        ) : (
          <AuditLine entry={audit} />
        )}
      </div>
      <Button type="button" variant="secondary" onClick={onDiscard} disabled={!dirty || saving}>
        Discard
      </Button>
      <Button type="button" onClick={onSave} disabled={!dirty || saving}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
}

export function IconButton({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-input text-text-muted hover:bg-surface-alt hover:text-error ${focusRing}`}
    >
      <i aria-hidden="true" className={icon} />
    </button>
  );
}
